import { useMemo } from 'react'

import { Tooltip, Typography } from '@mui/material'
import { MediaAsset } from '@nativewaves/nw-cloud-sdk/media'
import { ColumnDef } from '@tanstack/react-table'
import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'

import { CopyId } from '@shared/components/Table/Fields'
import { DATE_TIME_FORMAT } from '@shared/utils/support'
import { SortableHeader } from 'domains/MediaPlatforms/Assets'

export const useColumns = () => {
  const { t } = useTranslation(['common', 'domain'])

  return useMemo<ColumnDef<MediaAsset>[]>(
    () => [
      {
        accessorKey: 'name',
        header: () => <SortableHeader title={t('name')} sortProp="orderByName" />,
        meta: {
          sizing: {
            minWidth: 300,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Typography variant="inherit" noWrap>
            {getValue<string>()}
          </Typography>
        ),
      },
      {
        accessorKey: 'startTime',
        header: () => <SortableHeader title={t('domain:MediaPlatform.assets.startTime')} sortProp="orderByStartTime" />,
        meta: {
          sizing: {
            width: 200,
            px: 1,
          },
        },
        cell: ({ getValue }) => {
          const dateValue = getValue<string>()
          if (!dateValue) {
            return '-'
          }
          return format(new Date(getValue<string>()), DATE_TIME_FORMAT)
        },
      },
      {
        accessorKey: 'endTime',
        header: () => <SortableHeader title={t('domain:MediaPlatform.assets.endTime')} sortProp="orderByEndTime" />,
        meta: {
          sizing: {
            width: 200,
            px: 1,
          },
        },
        cell: ({ getValue }) => {
          const dateValue = getValue<string>()
          if (!dateValue) {
            return '-'
          }
          return format(new Date(getValue<string>()), DATE_TIME_FORMAT)
        },
      },
      {
        accessorKey: 'title',
        header: t('domain:MediaPlatform.assets.title'),
        meta: {
          sizing: {
            width: 200,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Tooltip title={getValue<string>()} placement="left" arrow>
            <Typography variant="inherit" noWrap>
              {getValue<string>()}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'descriptionLong',
        header: t('domain:MediaPlatform.assets.descriptionLong'),
        meta: {
          sizing: {
            width: 250,
            px: 2,
          },
        },
        cell: ({ getValue }) => (
          <Tooltip title={getValue<string>()} placement="left" arrow>
            <Typography variant="inherit" noWrap>
              {getValue<string>()}
            </Typography>
          </Tooltip>
        ),
      },
      {
        accessorKey: 'id',
        header: t('id'),
        meta: {
          sizing: {
            minWidth: 100,
            maxWidth: 250,
            px: 2,
          },
        },
        cell: ({ getValue }) => <CopyId id={getValue<string>()} />,
      },
    ],
    [t],
  )
}
